import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, api } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { CredibilityBadge } from '@/components/CredibilityBadge';
import { X, Heart, MapPin, Loader2, AlertTriangle, Flag, ShieldBan, ShieldCheck, SlidersHorizontal, Settings2, ChevronLeft, ChevronRight, ArrowLeft, Star, Undo2, CheckCircle2, Crown, MessageCircle, Briefcase, Building2, GraduationCap, Sparkles, Ruler, Users, UtensilsCrossed, PawPrint, Vote, Brain, Activity, WifiOff, Zap, Lock, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import RetentionCard from '@/components/RetentionCard';
import ProfileDetailRow from '../components/ProfileDetailRow';
import { formatDistance as fmtDist, distanceLabel, kmToDisplay, displayToKm, getUnit, formatHeight } from '@/utils/units';
import SendMessageDialog from '@/components/SendMessageDialog';
import DemoProfileBadge from '@/components/DemoProfileBadge';
import VerifiedBadge from '@/components/VerifiedBadge';
import PhotoLightbox from '@/components/PhotoLightbox';
import FullProfileBody from '@/components/FullProfileBody';
import SwipeCard from '@/components/SwipeCard';
import ProfileBoostBanner from '@/components/ProfileBoostBanner';
import MatchReferralNudge from '@/components/MatchReferralNudge';
import VideoAdModal from '@/components/VideoAdModal';
import InsightsBanner from '@/components/InsightsBanner';

const LOGO_URL = "/logo-wordmark.png";

// Feb 2026 Phase 2c — premium filter analytics. Fire-and-forget POST.
// Server discards unknown event names, so callers can't pollute the stream.
export const trackFilterEvent = (event, extra = {}) => {
  try { api.post('/analytics/filter-event', { event, ...extra }); } catch { /* noop */ }
};

// Default (empty) filter shape — used for reset + saved-search hydration.
const DEFAULT_FILTERS = {
  minAge: 18, maxAge: 99, maxDistance: 200,
  showMe: 'default', verifiedOnly: false, onlineOnly: false, minCompatibility: 0,
  drinking: '', smoking: '', cannabis: '', drugs: '', sleep_schedule: '',
  has_kids: '', wants_kids: '',
  monogamy: '', marital_history: '', marital_status: '',
  willing_to_relocate: '',
  active_recency: '',
  complete_profiles_only: false,
  // Feb 27 2026 · Phase 164 — VIP filters wired: freshness + activity.
  new_members: false,
  hide_inactive: false,
  relationshipGoal: '', religion: '', education: '',
  race: [], bodyType: [], diet: [], pets: '', politics: [],
  sexualOrientation: '',
  industry: '', language: '',
  personalityType: '', zodiacSign: '', exercise: '',
  interest: '',  // Feb 2026 — single-interest filter (server does $in on interests array)
  // Feb 2026 — VIP compat-pillar filters. Same source of truth as compat score.
  loveLanguage: '', communicationStyle: '', familyPlans: '', socialEnergy: '',
  // Feb 2026 — `intent` filter removed; options folded into relationshipGoal.
  meetingReadiness: '', dateType: '', readyOnly: false,
  minHeight: 140, maxHeight: 220,
};

// getPhotoUrl re-exported from SwipeCard to keep helper co-located with its
// primary consumer. Discover uses it only for the "next card preview" image
// stacked underneath the current card.
import { getPhotoUrl } from '@/components/SwipeCard';
import ConfettiBurst from '@/components/ConfettiBurst';
import { playCelebrationChime } from '@/lib/celebrationChime';

// Small circular avatar used by the "It's a Match" celebration dialog to
// render both parties side-by-side (Feb 26 2026). Falls back to the initial
// letter on a brand-red background when `photoUrl` is null (user has no photo).
function MatchAvatar({ photoUrl, name, style, className }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <div
      className={`w-24 h-24 rounded-full bg-white flex items-center justify-center ${className || ''}`}
      style={{ boxShadow: '0 8px 24px rgba(15,15,16,0.18)', border: '4px solid #fff', outline: '2px solid rgba(185,28,28,0.18)', ...style }}
      data-testid="match-avatar"
    >
      {photoUrl ? (
        <img src={photoUrl} alt={name || ''} className="w-full h-full rounded-full object-cover" />
      ) : (
        <div className="w-full h-full rounded-full brand-gradient flex items-center justify-center text-2xl font-semibold text-white">
          {initial}
        </div>
      )}
    </div>
  );
}

export default function Discover() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [idx, setIdx] = useState(0);
  const [swipeInfo, setSwipeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(false);
  const [matchDialog, setMatchDialog] = useState(null);
  // Fire the celebration chime the instant the "It's a Match" dialog opens.
  // Feb 26 2026 · pairs with the confetti burst for a proper "yes!" beat.
  useEffect(() => {
    if (matchDialog) playCelebrationChime('match');
  }, [matchDialog]);
  // Start index for the full-screen PhotoLightbox (the actual carousel
  // position inside the SwipeCard is owned by SwipeCard).
  const [lightboxStartIdx, setLightboxStartIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  // Feb 27 2026 · Phase 164 — Tier-upgrade celebration state. When the user's
  // tier jumps since their last visit to the filter drawer, we auto-expand
  // the newly-unlocked section + fade its lock badge + show a toast. Only
  // fires once per upgrade (dedupe via localStorage `last_seen_tier`).
  const [upgradeCelebration, setUpgradeCelebration] = useState(null);  // 'premium' | 'vip' | null
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activeFilters, setActiveFilters] = useState({});
  const [breakdown, setBreakdown] = useState(null);
  const [promptsList, setPromptsList] = useState([]);
  // Feb 2026 · dynamic filter catalogs. Previously the Personality dropdown
  // was hardcoded to the pre-rework 10-item list, which drifted when we
  // changed the catalog. Now both catalogs are fetched from their /api
  // endpoints (same source of truth as the setup wizard) so future edits
  // propagate automatically. Interests filter is brand-new.
  const [personalityCatalog, setPersonalityCatalog] = useState([]);
  const [interestsCatalog, setInterestsCatalog] = useState([]);
  const [politicsCatalog, setPoliticsCatalog] = useState([]);
  // Feb 2026 · four more filter dropdowns migrated to dynamic fetch to end
  // the same hardcoded-drift bug that had personality showing a stale list.
  const [religionsCatalog, setReligionsCatalog] = useState([]);
  const [educationCatalog, setEducationCatalog] = useState([]);
  const [industriesCatalog, setIndustriesCatalog] = useState([]);
  const [languagesCatalog, setLanguagesCatalog] = useState([]);
  const loadingMoreRef = useRef(false);
  // Feb 2026 UX polish — track the last fetch outcome so the empty state
  // can distinguish "no profiles today" vs "network down" vs "filters too
  // strict" vs "you need to complete your profile". Each case gets a
  // different message + call-to-action.
  const [fetchError, setFetchError] = useState(null); // 'network' | 'server' | null
  const [locationMissing, setLocationMissing] = useState(false);
  // Feb 2026 premium filter system — locked-filter paywall.
  // When user taps a locked filter, we open a modal with the appropriate
  // upgrade target. Also used to catch server-side 403s when tier state
  // is stale (e.g. right after downgrade).
  const [lockModal, setLockModal] = useState(null); // { label, target: 'premium'|'vip' } | null

  // Feb 2026 Phase 2b — Saved Searches state.
  //   savedSearches: [] loaded from GET /saved-searches
  //   savedQuota: { tier, quota, used } — surfaced in the Save dialog CTA
  //   savedSearchOpen: whether the saved-search rename/create sheet is showing
  //   defaultAppliedRef: one-shot gate so the default is auto-applied only once
  //     per Discover mount (subsequent filter dialog opens do NOT re-apply)
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedQuota, setSavedQuota] = useState({ tier: 'free', quota: 1, used: 0 });
  const [savingSearch, setSavingSearch] = useState(false);
  const [newSearchName, setNewSearchName] = useState('');
  const [editingSearchId, setEditingSearchId] = useState(null);
  const [editingSearchName, setEditingSearchName] = useState('');
  const defaultAppliedRef = useRef(false);

  // Feb 2026 — Video ad slot for free-tier users. Cadence + config from
  // GET /ads/config; swipe counter lives in sessionStorage so it survives
  // navigation but resets per tab. Only free-tier users ever see an ad.
  const [adConfig, setAdConfig] = useState(null);
  const [adOpen, setAdOpen] = useState(false);
  const nextAdThresholdRef = useRef(null);
  const swipesSinceAdRef = useRef(
    Number(sessionStorage.getItem('mtk_swipes_since_ad') || 0)
  );
  const pendingSwipeRef = useRef(null);

  // Boost status — polled every 10s while active so the countdown chip
  // stays fresh. When inactive, we still poll once on mount + on foreground
  // so we know whether to show the chip at all.
  const [boostStatus, setBoostStatus] = useState(null);
  // Client-side ticker (1 Hz) — derives a live mm:ss countdown from the
  // server's `seconds_remaining` without hammering the API every second.
  // We compute an absolute expiry timestamp when the status arrives and
  // decrement from `Date.now()`. When it hits 0 we re-fetch once so the
  // header pill flips back to the plain lightning icon.
  const boostExpiryRef = useRef(null);
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    let mounted = true;
    const pollBoost = async () => {
      try {
        const r = await api.get('/boost/status');
        if (!mounted) return;
        setBoostStatus(r.data);
        boostExpiryRef.current = r.data?.active && r.data?.seconds_remaining
          ? Date.now() + r.data.seconds_remaining * 1000
          : null;
      } catch { /* silent — non-critical */ }
    };
    pollBoost();
    // Only poll the server every 10s if active.
    const poll = setInterval(() => {
      if (boostStatus?.active) pollBoost();
    }, 10000);
    // 1 Hz local ticker — only mounted while a boost is running.
    let tick = null;
    if (boostStatus?.active) {
      tick = setInterval(() => setNowTick(Date.now()), 1000);
    }
    return () => { mounted = false; clearInterval(poll); if (tick) clearInterval(tick); };
  }, [boostStatus?.active]);
  const boostSecondsLeft = boostExpiryRef.current
    ? Math.max(0, Math.floor((boostExpiryRef.current - nowTick) / 1000))
    : 0;
  // When the ticker hits 0, refresh once so the header + /boost page flip.
  useEffect(() => {
    if (boostStatus?.active && boostSecondsLeft === 0 && boostExpiryRef.current) {
      boostExpiryRef.current = null;
      api.get('/boost/status').then(r => setBoostStatus(r.data)).catch(() => {});
    }
  }, [boostSecondsLeft, boostStatus?.active]);
  const fmtBoostTime = (s) => {
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${m}:${ss.toString().padStart(2, '0')}`;
  };

  // Scroll-position memory (Feb 2026 UX polish).
  // Feb 2026 Discover refresh — the swipe card itself is now the vertical
  // scroller (killed the tap-to-open modal pattern). We save scroll top
  // before a swipe so Undo restores the user's exact reading position.
  const cardScrollRef = useRef(null);
  const scrollMemory = useRef({}); // { [user_id]: scrollTop }
  const saveCurrentScroll = () => {
    const el = cardScrollRef.current;
    const uid = profiles[idx]?.user_id;
    if (el && uid) scrollMemory.current[uid] = el.scrollTop;
  };

  // Drag motion values now live inside SwipeCard.

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    const f = activeFilters;
    if (f.minAge && f.minAge > 18) params.set('min_age', f.minAge);
    if (f.maxAge && f.maxAge < 99) params.set('max_age', f.maxAge);
    if (f.maxDistance && f.maxDistance < 200) params.set('max_distance', f.maxDistance);
    if (f.showMe && f.showMe !== 'default') params.set('show_me', f.showMe);
    if (f.verifiedOnly) params.set('verified_only', 'true');
    if (f.onlineOnly) params.set('online_only', 'true');
    if (f.minCompatibility) params.set('min_compatibility', f.minCompatibility);
    if (f.drinking) params.set('drinking', f.drinking);
    if (f.smoking) params.set('smoking', f.smoking);
    if (f.has_kids) params.set('has_kids', f.has_kids);
    if (f.wants_kids) params.set('wants_kids', f.wants_kids);
    // Feb 2026 — user top-3 additions
    if (f.monogamy) params.set('monogamy', f.monogamy);
    if (f.marital_history) params.set('marital_history', f.marital_history);
    if (f.marital_status) params.set('marital_status', f.marital_status);
    if (f.cannabis) params.set('marijuana', f.cannabis);
    if (f.drugs) params.set('drugs', f.drugs);
    if (f.sleep_schedule) params.set('sleep_schedule', f.sleep_schedule);
    // Feb 2026 follow-up batch (relocation + activity + completeness)
    if (f.willing_to_relocate) params.set('willing_to_relocate', f.willing_to_relocate);
    if (f.active_recency) params.set('active_recency', f.active_recency);
    if (f.complete_profiles_only) params.set('complete_profiles_only', '1');
    // Feb 27 2026 · Phase 164 — VIP freshness + activity filters.
    if (f.new_members) params.set('new_members', 'true');
    if (f.hide_inactive) params.set('hide_inactive', 'true');
    if (f.relationshipGoal) params.set('relationship_goal', f.relationshipGoal);
    if (f.religion) params.set('religion', f.religion);
    if (f.education) params.set('education', f.education);
    // Feb 2026 · Phase 139 — race is a multi-select. Comma-separated on
    // the wire; backend supports both scalar and comma-list.
    if (Array.isArray(f.race) && f.race.length) {
      params.set('race', f.race.join(','));
    } else if (typeof f.race === 'string' && f.race) {
      params.set('race', f.race);
    }
    // Feb 2026 · Phase 137 — bodyType is a multi-select. Serialize as a
    // comma-separated list; backend accepts `?body_type=athletic,average`.
    if (Array.isArray(f.bodyType) && f.bodyType.length) {
      params.set('body_type', f.bodyType.join(','));
    } else if (typeof f.bodyType === 'string' && f.bodyType) {
      // Legacy fallback in case someone hydrated an older string value.
      params.set('body_type', f.bodyType);
    }
    // Feb 2026 · Phase 140 — diet + politics multi-select. Same
    // comma-separated wire encoding as body_type / race.
    if (Array.isArray(f.diet) && f.diet.length) params.set('diet', f.diet.join(','));
    else if (typeof f.diet === 'string' && f.diet) params.set('diet', f.diet);
    if (f.pets) params.set('pets', f.pets);
    if (Array.isArray(f.politics) && f.politics.length) params.set('politics', f.politics.join(','));
    else if (typeof f.politics === 'string' && f.politics) params.set('politics', f.politics);
    if (f.sexualOrientation) params.set('sexual_orientation', f.sexualOrientation);
    if (f.industry) params.set('industry', f.industry);
    if (f.language) params.set('languages', f.language);
    if (f.personalityType) params.set('personality_type', f.personalityType);
    if (f.interest) params.set('interests', f.interest);  // Feb 2026 — server expects comma-separated; single value works
    if (f.zodiacSign) params.set('zodiac_sign', f.zodiacSign);
    if (f.exercise) params.set('exercise', f.exercise);
    // Feb 2026 — VIP compat-pillar filters
    if (f.loveLanguage) params.set('love_language', f.loveLanguage);
    if (f.communicationStyle) params.set('communication_style', f.communicationStyle);
    if (f.familyPlans) params.set('family_plans', f.familyPlans);
    if (f.socialEnergy) params.set('social_energy', f.socialEnergy);
    if (f.intent) params.set('intent', f.intent); // Feb 2026 removed from UI; retained for legacy state hydration
    if (f.meetingReadiness) params.set('meeting_readiness', f.meetingReadiness);
    if (f.dateType) params.set('date_type', f.dateType);
    if (f.readyOnly) params.set('ready_only', 'true');
    if (f.minHeight && f.minHeight > 140) params.set('min_height', f.minHeight);
    if (f.maxHeight && f.maxHeight < 220) params.set('max_height', f.maxHeight);
    return params;
  }, [activeFilters]);

  const fetch = useCallback(async () => {
    try {
      setFetchError(null);
      const params = buildFilterParams();
      params.set('limit', '20');
      const r = await api.get(`/discover?${params.toString()}`);
      setProfiles(r.data.profiles); setSwipeInfo(r.data.swipe_info); setIdx(0);
      // Location detection: if the server didn't rank by distance, that
      // usually means my_profile has no lat/lng. Surface a soft banner so
      // the user can go fix it in Settings.
      const firstWithDist = (r.data.profiles || []).find(p => typeof p.distance_km === 'number');
      setLocationMissing(!firstWithDist && (r.data.profiles || []).length > 0);
    } catch (e) {
      // 400 → profile incomplete, send them to onboarding
      if (e.response?.status === 400) {
        navigate('/profile/setup', { replace: true });
        return;
      }
      // 401 → session expired, send them to login
      if (e.response?.status === 401) {
        navigate('/', { replace: true });
        return;
      }
      // 403 → filter tier gate hit. Read X-Upgrade-Target header to open the
      // right paywall. Falls back to premium if the header is missing.
      if (e.response?.status === 403) {
        const target = e.response.headers?.['x-upgrade-target'] || 'premium';
        setLockModal({ label: 'These filters', target });
        trackFilterEvent('paywall_shown_from_filter', { source: 'server_403', target_tier: target, tier: swipeInfo?.tier });
        setProfiles([]); setSwipeInfo(null);
        return;
      }
      // No response = the request never got a reply (offline / DNS / CORS
      // block / server down). Everything else is treated as a server error.
      setFetchError(e.response ? 'server' : 'network');
      setProfiles([]);
      setSwipeInfo(null);
    }
    finally { setLoading(false); }
  }, [navigate, buildFilterParams]);

  // Infinite scroll: prefetch the next batch when deck runs low, dedupe against current profiles.
  const fetchMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    try {
      const params = buildFilterParams();
      params.set('limit', '20');
      const r = await api.get(`/discover?${params.toString()}`);
      if (r.data.swipe_info) setSwipeInfo(r.data.swipe_info);
      setProfiles(prev => {
        const seen = new Set(prev.map(p => p.user_id));
        const fresh = (r.data.profiles || []).filter(p => !seen.has(p.user_id));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
    } catch (e) { /* silent — next swipe will retry */ }
    finally { loadingMoreRef.current = false; }
  }, [buildFilterParams]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { api.get('/prompts').then(r => setPromptsList(r.data.prompts)).catch(() => {}); }, []);
  // Feb 2026 · fetch filter catalogs from the same endpoints the setup
  // wizard consumes. If the fetch fails we leave the catalog empty →
  // the FilterSelect just shows "Any", which is safer than showing
  // stale/hardcoded options.
  useEffect(() => {
    api.get('/personality-types').then(r => setPersonalityCatalog(r.data.personality_types || [])).catch(() => {});
    api.get('/interests').then(r => setInterestsCatalog(r.data.interests || [])).catch(() => {});
    api.get('/politics-options').then(r => setPoliticsCatalog(r.data.politics || [])).catch(() => {});
    api.get('/religions').then(r => setReligionsCatalog(r.data.religions || [])).catch(() => {});
    api.get('/education-levels').then(r => setEducationCatalog(r.data.education_levels || [])).catch(() => {});
    api.get('/industries').then(r => setIndustriesCatalog(r.data.industries || [])).catch(() => {});
    api.get('/languages').then(r => setLanguagesCatalog(r.data.languages || [])).catch(() => {});
  }, []);

  // Prefetch more when deck runs low (5 cards before the end).
  useEffect(() => {
    if (!loading && profiles.length > 0 && idx >= profiles.length - 5) {
      fetchMore();
    }
  }, [idx, profiles.length, loading, fetchMore]);

  const swipe = async (dir) => {
    if (swiping || idx >= profiles.length) return;
    const cur = profiles[idx];
    if (dir === 'right' && swipeInfo && !swipeInfo.can_swipe_right) {
      toast.error(swipeInfo.limit_message);
      return;
    }
    if (dir === 'super' && swipeInfo && swipeInfo.super_likes_remaining <= 0) {
      toast.error(`Out of super likes today. ${swipeInfo.tier === 'free' ? 'Upgrade for more.' : 'Resets tomorrow.'}`);
      return;
    }
    // Save the scroll position of the profile we're leaving. On undo we'll
    // restore this exact spot so the user picks up where they left off.
    saveCurrentScroll();
    setSwiping(true);
    let pendingSwipeStats = null;
    try {
      const r = await api.post('/swipe', { swiped_id: cur.user_id, direction: dir });
      // Feb 2026 anti-twitch — defer swipe_stats update until AFTER the card
      // exit animation, otherwise the badge counters + button disabled states
      // re-render on the outgoing card mid-flight and the whole deck flickers.
      pendingSwipeStats = r.data.swipe_stats;
      if (r.data.match) setTimeout(() => setMatchDialog(r.data.match_data), 250);
    } catch (e) {
      // Backend now returns 409 for duplicate swipes on the same target
      // (Feb 2026 audit fix). If we hit this, silently advance past the
      // stale card instead of surfacing a scary error.
      if (e.response?.status === 409) {
        setTimeout(() => { setIdx(i => i + 1); setSwiping(false); }, 280);
        return;
      }
      // Feb 2026 — Route structured paywall bodies (super_like_locked,
      // super_like_cap_hit, etc.) to /subscription instead of surfacing
      // as a dead-end toast.
      const detail = e.response?.data?.detail;
      if ((e.response?.status === 403 || e.response?.status === 429) && detail && typeof detail === 'object' && detail.code) {
        navigate(`/subscription?feature=${detail.feature || 'super_like'}&reason=${detail.code}`);
        setSwiping(false);
        return;
      }
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed'));
      setSwiping(false);
      return;
    }
    setTimeout(() => {
      setIdx(i => i + 1); setSwiping(false);
      if (pendingSwipeStats) setSwipeInfo(p => ({ ...p, ...pendingSwipeStats }));
      // After the card leaves the deck, check if it's ad time (free tier only).
      maybeShowAd();
    }, 280);
  };

  const undo = async () => {
    // Feb 2026 — tier gating happens server-side; the button routes to the
    // upgrade screen on 403 rather than swallowing the click.
    if (!swipeInfo?.can_undo) {
      navigate('/subscription?feature=undo&reason=undo_locked');
      return;
    }
    // Client-side hint when Premium has exhausted today's 3 rewinds.
    if (
      swipeInfo?.undo_daily_cap != null &&
      swipeInfo.undo_daily_cap < 999999 &&
      (swipeInfo.undo_daily_remaining ?? 0) <= 0
    ) {
      navigate('/subscription?feature=undo&reason=undo_cap_hit');
      return;
    }
    try {
      const r = await api.post('/swipe/undo');
      if (r.data.restored_profile) {
        setProfiles(p => [r.data.restored_profile, ...p.slice(idx)]);
        setIdx(0);
        toast.success('Swipe undone');
      }
      // Refresh swipe info so the counter tile ticks down immediately.
      api.get('/swipe/info').then(res => setSwipeInfo(res.data)).catch(() => {});
    } catch (e) {
      // 410 = undo window expired (Feb 2026 audit — 5 min max).
      if (e.response?.status === 410) {
        toast.error('Too late to undo — swipe window has passed.');
        return;
      }
      // Feb 2026 — server now returns a structured paywall body on 403.
      // Route to /subscription with the reason so the upgrade screen can
      // render tier-specific copy and highlight the right plan tile.
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail && typeof detail === 'object' && detail.code) {
        navigate(`/subscription?feature=${detail.feature || 'undo'}&reason=${detail.code}`);
        return;
      }
      toast.error(
        (typeof detail === 'string' ? detail : detail?.message) || 'Nothing to undo'
      );
    }
  };

  // Load compatibility breakdown when the current profile changes.
  // (Was previously gated on the detail dialog open state; now inline.)
  useEffect(() => {
    if (profiles[idx]) {
      setBreakdown(null);
      api.get(`/compatibility/${profiles[idx].user_id}`)
        .then(r => setBreakdown(r.data))
        .catch(() => setBreakdown(null));
    }
  }, [idx, profiles]);

  // Restore or reset the card's inline scroll position when the current
  // profile changes (Feb 2026 UX polish).
  //   • Fresh view of a profile → scroll to top
  //   • After Undo → restore the exact scroll position they left the
  //     previous profile at (saved via saveCurrentScroll before swiping)
  useEffect(() => {
    const el = cardScrollRef.current;
    const uid = profiles[idx]?.user_id;
    if (!el || !uid) return;
    const saved = scrollMemory.current[uid];
    // Use rAF so the DOM has re-rendered the new profile's content before
    // we jump — otherwise scrollTop clamps against the OLD content height.
    requestAnimationFrame(() => {
      el.scrollTop = typeof saved === 'number' ? saved : 0;
    });
  }, [idx, profiles]);

  const block = async (uid) => { try { await api.post(`/block/${uid}`); toast.success('Blocked'); setIdx(i => i + 1); } catch {} };
  const report = async (uid, reason) => { try { await api.post(`/report/${uid}`, { reason, description: '' }); toast.success('Reported'); setReportOpen(false); } catch {} };

  // ==================== Saved Searches (Feb 2026 Phase 2b) ====================
  // Load presets once on mount. If there's a default preset AND the user
  // hasn't already applied filters this session, auto-apply it so returning
  // users get their preferred deck without needing to open the drawer.
  const loadSavedSearches = useCallback(async () => {
    try {
      const r = await api.get('/saved-searches');
      const rows = r.data.saved_searches || [];
      setSavedSearches(rows);
      setSavedQuota({
        tier: r.data.tier || 'free',
        quota: r.data.quota ?? 1,
        used: r.data.used ?? rows.length,
      });
      return rows;
    } catch { return []; }
  }, []);

  useEffect(() => {
    (async () => {
      const rows = await loadSavedSearches();
      if (defaultAppliedRef.current) return;
      const def = (rows || []).find(r => r.is_default);
      if (def && def.filters && Object.keys(def.filters).length > 0) {
        defaultAppliedRef.current = true;
        // Merge onto DEFAULT_FILTERS so partially-saved presets don't crash sliders.
        const merged = { ...DEFAULT_FILTERS, ...def.filters };
        setFilters(merged);
        setActiveFilters(def.filters);
        trackFilterEvent('saved_search_loaded', { saved_search_id: def.search_id, tier: savedQuota.tier });
        // Best-effort mark used (server updates last_used_at).
        api.post(`/saved-searches/${def.search_id}/use`).catch(() => {});
      }
    })();
  }, [loadSavedSearches]);

  // Fetch the ad slot config once (free-tier only path uses it).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/ads/config');
        if (!cancelled && r.data?.enabled) {
          setAdConfig(r.data);
          // Roll the next threshold in [min_swipes, max_swipes]
          const min = r.data.min_swipes || 20;
          const max = r.data.max_swipes || 25;
          nextAdThresholdRef.current = min + Math.floor(Math.random() * (max - min + 1));
        }
      } catch { /* noop — ads are optional */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Called after each swipe. If the user is on free tier AND has hit the
  // random threshold, open the ad modal. Otherwise no-op.
  const maybeShowAd = useCallback(() => {
    if (!adConfig?.enabled) return;
    if (swipeInfo?.tier && swipeInfo.tier !== 'free') return;
    swipesSinceAdRef.current += 1;
    sessionStorage.setItem('mtk_swipes_since_ad', String(swipesSinceAdRef.current));
    if (swipesSinceAdRef.current >= (nextAdThresholdRef.current || 20)) {
      swipesSinceAdRef.current = 0;
      sessionStorage.setItem('mtk_swipes_since_ad', '0');
      // Roll next threshold for the following slot
      const min = adConfig.min_swipes || 20;
      const max = adConfig.max_swipes || 25;
      nextAdThresholdRef.current = min + Math.floor(Math.random() * (max - min + 1));
      setAdOpen(true);
    }
  }, [adConfig, swipeInfo?.tier]);


  const applySavedSearch = async (s) => {
    const merged = { ...DEFAULT_FILTERS, ...(s.filters || {}) };
    setFilters(merged);
    setActiveFilters(s.filters || {});
    trackFilterEvent('saved_search_loaded', { saved_search_id: s.search_id, tier: savedQuota.tier });
    try { await api.post(`/saved-searches/${s.search_id}/use`); } catch { /* non-blocking */ }
    toast.success(`Loaded "${s.name}"`);
    setFilterOpen(false);
  };

  const saveCurrentAsSearch = async () => {
    const name = (newSearchName || '').trim();
    if (!name) { toast.error('Give this search a name'); return; }
    // Persist only fields that differ from DEFAULT_FILTERS to keep the doc tiny.
    const diff = {};
    for (const [k, v] of Object.entries(filters)) {
      if (JSON.stringify(v) !== JSON.stringify(DEFAULT_FILTERS[k])) diff[k] = v;
    }
    try {
      const r = await api.post('/saved-searches', { name, filters: diff, is_default: false });
      setSavedSearches(prev => [r.data, ...prev]);
      setSavedQuota(q => ({ ...q, used: q.used + 1 }));
      setNewSearchName('');
      setSavingSearch(false);
      trackFilterEvent('saved_search_created', { saved_search_id: r.data.search_id, tier: savedQuota.tier });
      toast.success(`Saved "${name}"`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(e.response.data?.detail || 'Upgrade to save more searches.');
        setLockModal({ label: 'Saved searches', target: savedQuota.tier === 'free' ? 'premium' : 'vip' });
      } else {
        toast.error('Could not save.');
      }
    }
  };

  const deleteSavedSearch = async (s) => {
    try {
      await api.delete(`/saved-searches/${s.search_id}`);
      setSavedSearches(prev => prev.filter(x => x.search_id !== s.search_id));
      setSavedQuota(q => ({ ...q, used: Math.max(0, q.used - 1) }));
      trackFilterEvent('saved_search_deleted', { saved_search_id: s.search_id, tier: savedQuota.tier });
    } catch {
      toast.error('Delete failed');
    }
  };

  const setDefaultSavedSearch = async (s) => {
    try {
      await api.patch(`/saved-searches/${s.search_id}`, { is_default: !s.is_default });
      setSavedSearches(prev => prev.map(x => ({
        ...x,
        is_default: x.search_id === s.search_id ? !s.is_default : false,
      })));
      trackFilterEvent('saved_search_set_default', { saved_search_id: s.search_id, tier: savedQuota.tier });
    } catch {
      toast.error('Could not update default');
    }
  };

  const renameSavedSearch = async (s) => {
    const nn = (editingSearchName || '').trim();
    if (!nn || nn === s.name) { setEditingSearchId(null); return; }
    try {
      const r = await api.patch(`/saved-searches/${s.search_id}`, { name: nn });
      setSavedSearches(prev => prev.map(x => x.search_id === s.search_id ? r.data : x));
      setEditingSearchId(null);
    } catch {
      toast.error('Rename failed');
    }
  };

  // Unified helper: opens the paywall AND fires the two matching analytics
  // events so we can distinguish "user tapped a locked chip" from "modal
  // opened". Called from FilterSelect + inline VIP toggles.
  const openLockPaywall = ({ label, target, filter_key }) => {
    trackFilterEvent('locked_filter_tapped', { filter_key, target_tier: target, tier: swipeInfo?.tier });
    trackFilterEvent('paywall_shown_from_filter', { source: 'client', filter_key, target_tier: target, tier: swipeInfo?.tier });
    setLockModal({ label, target, filter_key });
  };


  // handleDragEnd moved into SwipeCard along with the motion values.


  if (loading) return <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" /></div>;

  const cur = profiles[idx];
  const noMore = !cur;
  // Photos list — only needed by the parent for the full-screen PhotoLightbox
  // and the "next card preview" thumbnail. The SwipeCard computes its own
  // slides/photos internally (including the video-first ordering).
  const photos = cur
    ? (cur.photos?.length ? cur.photos : [cur.photo_url]).filter(Boolean)
    : [];

  return (
    <div className="min-h-screen bg-[var(--cream)] pb-[80px]" data-testid="discover-page">
      {swipeInfo?.limit_message && (
        <div className="mx-4 mt-3 rounded-xl bg-[rgba(185,28,28,0.08)] border border-[rgba(185,28,28,0.2)] px-4 py-2.5 flex items-center gap-2" data-testid="swipe-limit-warning">
          <AlertTriangle className="w-4 h-4 text-[#B91C1C] flex-shrink-0" />
          <p className="text-xs text-[#7F1D1D]">{swipeInfo.limit_message}</p>
        </div>
      )}
      {/* Dating Insights nudge — behavioral coaching banner. Fetched from
          /api/insights/nudges. Dismissible (7-day snooze). Renders nothing
          when the user doesn't trip any rule. */}
      <InsightsBanner />
      {/* Location-missing banner removed per user request (Feb 2026). If
          the user has no location set, distance sort is silently skipped;
          they can still add it via Settings. */}

      <div className="px-5 pt-4 pb-3 flex items-center justify-between">
        <img src={LOGO_URL} alt="Meet to Keep" className="h-20 w-auto object-contain" data-testid="discover-logo" />
        <div className="flex items-center gap-2">
          {/* City pill — shows the user's own location so they can sanity-check
              what they're browsing near. Feb 2026 header refresh. */}
          {(user?.profile?.location?.city || user?.location?.city) && (
            <span
              data-testid="discover-my-city-pill"
              className="hidden sm:inline-flex items-center gap-1.5 h-10 px-3 rounded-full text-[12px] font-[700] text-[#0F0F10] bg-white"
              style={{ border: '1px solid rgba(15,15,16,0.08)' }}
              title="Your current location"
            >
              <MapPin size={12} strokeWidth={2.2} />
              <span className="truncate max-w-[140px]">
                {user?.profile?.location?.city || user?.location?.city}
              </span>
            </span>
          )}
          {/* Boost button — icon-only when idle, expands to a live mm:ss
              chip when a boost is running. Feb 2026 header refresh. */}
          <button
            onClick={() => navigate('/boost')}
            data-testid="discover-boost-btn"
            aria-label={boostStatus?.active ? `Boost active · ${fmtBoostTime(boostSecondsLeft)} left` : 'Meet Priority Boost'}
            title={boostStatus?.active
              ? `Boost active · ${fmtBoostTime(boostSecondsLeft)} left`
              : 'Get seen by 10× more people'}
            className={`h-10 rounded-full flex items-center justify-center transition-all active:scale-95 relative ${
              boostStatus?.active ? 'px-3 gap-1.5' : 'w-10'
            }`}
            style={{
              background: boostStatus?.active
                ? 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)'
                : '#DC2626',
              boxShadow: boostStatus?.active
                ? '0 4px 10px rgba(245, 158, 11, 0.4)'
                : '0 4px 8px rgba(220, 38, 38, 0.30)',
            }}
          >
            <Zap size={17} className="text-white" fill="currentColor" strokeWidth={2.2} />
            {boostStatus?.active && (
              <span
                data-testid="discover-boost-timer"
                className="text-white text-[12px] font-[800] tabular-nums leading-none"
              >
                {fmtBoostTime(boostSecondsLeft)}
              </span>
            )}
          </button>
          <button onClick={() => {
              // Feb 27 2026 · Phase 164 — Detect tier upgrade since last visit
              // to the drawer and stage the celebration. Falls back cleanly
              // when localStorage is unavailable (private mode etc.).
              try {
                const currentTier = swipeInfo?.tier || 'free';
                const seen = localStorage.getItem('mtk_last_seen_filter_tier') || 'free';
                if (tierRank(currentTier) > tierRank(seen)) {
                  setUpgradeCelebration(currentTier);   // 'premium' | 'vip'
                  toast.success(
                    currentTier === 'vip'
                      ? 'Your Best Matches filters just unlocked ✨'
                      : 'Your Type filters just unlocked ✨',
                    { duration: 4500 }
                  );
                }
                localStorage.setItem('mtk_last_seen_filter_tier', currentTier);
              } catch { /* private-mode / storage-denied — silent */ }
              setFilterOpen(true);
            }} data-testid="open-filters"
            className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-colors ${
              Object.keys(activeFilters).length > 0 ? 'border-[#B91C1C]/40 bg-[#B91C1C]/08 text-[#B91C1C]' : 'border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02]'}`}>
            <SlidersHorizontal size={17} strokeWidth={1.75} />
          </button>
          <button onClick={() => navigate('/settings')} data-testid="open-settings"
            className="w-10 h-10 rounded-xl flex items-center justify-center border border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02] transition-colors">
            <Settings2 size={17} strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* Quick-access Verified chip — one-tap toggle for the ID-verified feed.
          Same VIP gate as the drawer toggle; when the user isn't VIP we pop
          the paywall instead of silently flipping the state. Always visible so
          safety-conscious daters can lock the feed without opening filters. */}
      {!noMore && !loading && (
        <div className="px-5 mt-1 mb-1 flex items-center gap-2 flex-wrap" data-testid="discover-quick-chip-row">
          <button
            type="button"
            data-testid="quick-chip-verified-only"
            onClick={() => {
              if (tierRank(swipeInfo?.tier) < 2) {
                openLockPaywall({ label: 'Verified profiles only', target: 'vip', filter_key: 'verifiedOnly' });
                return;
              }
              setFilters((f) => ({ ...f, verifiedOnly: !f.verifiedOnly }));
            }}
            aria-pressed={filters.verifiedOnly}
            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] transition-colors border ${
              filters.verifiedOnly
                ? 'bg-[#1E4FD8] text-white border-[#1E4FD8]'
                : 'bg-white text-[#0F0F10] border-[var(--hairline-strong)] hover:bg-black/[0.03]'
            }`}
            title="Show only ID-verified profiles"
          >
            <CheckCircle2 size={12} strokeWidth={2.4} />
            <span>Verified only</span>
            {tierRank(swipeInfo?.tier) < 2 && (
              <Lock size={10} className={filters.verifiedOnly ? 'text-white/90' : 'text-[#B91C1C]'} />
            )}
          </button>
        </div>
      )}

      {/* Bonus swipes chip — earned via ad rewards. Only shown when > 0.
          (Removed: Super-Likes-today pill, Get-Verified banner, Add-your-
          location banner, and the persistent Super-Like/Boost quota chip
          per direct user feedback — Feb 2026.) */}
      {!noMore && !loading && swipeInfo?.bonus_swipes > 0 && (
        <div className="px-5 mt-1 mb-2 flex items-center gap-2 flex-wrap" data-testid="quota-chip-row">
          <div
            data-testid="quota-chip-bonus-swipes"
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.1em]
                       bg-[#FEF3C7] text-[#B45309] border border-[#FDE68A]"
            title="Extra swipes earned by watching an ad"
          >
            <Sparkles size={12} strokeWidth={2.4} />
            <span>{swipeInfo.bonus_swipes} bonus swipe{swipeInfo.bonus_swipes === 1 ? '' : 's'}</span>
          </div>
        </div>
      )}

      {/* Orchestrated nudge: rotates verify → video → upgrade based on what's missing.
          Self-suppresses (returns null) once the user has all three. */}
      {!noMore && (
        <div className="px-5 mb-3">
          <ProfileBoostBanner />
        </div>
      )}

      {noMore ? (
        <div className="flex items-center justify-center h-[calc(100vh-160px)]" data-testid="discover-empty">
          <div className="text-center px-6 max-w-sm">
            {/* ==================== Distinct empty states ==================== */}
            {fetchError === 'network' ? (
              // No response reached the server — offline, DNS, or blocked.
              <>
                <div className="w-20 h-20 rounded-full bg-[var(--cream-deep)] border border-[var(--hairline)] flex items-center justify-center mx-auto mb-5">
                  <WifiOff className="w-8 h-8 text-[#B91C1C]" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-[600] mb-2" data-testid="empty-network-title">You&apos;re offline</h2>
                <p className="text-[#4B4B52] text-sm mb-6">
                  We couldn&apos;t reach Meet to Keep. Check your connection and try again.
                </p>
                <button onClick={fetch} data-testid="retry-network-btn"
                  className="px-6 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold btn-shine">
                  Retry
                </button>
              </>
            ) : fetchError === 'server' ? (
              // Server responded with an error (5xx / unexpected 4xx).
              <>
                <div className="w-20 h-20 rounded-full bg-[var(--cream-deep)] border border-[var(--hairline)] flex items-center justify-center mx-auto mb-5">
                  <AlertTriangle className="w-8 h-8 text-[#B91C1C]" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-[600] mb-2" data-testid="empty-server-title">Something went wrong</h2>
                <p className="text-[#4B4B52] text-sm mb-6">
                  We&apos;re having trouble loading profiles right now. Hang tight and try again in a moment.
                </p>
                <button onClick={fetch} data-testid="retry-server-btn"
                  className="px-6 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold btn-shine">
                  Try again
                </button>
              </>
            ) : Object.keys(activeFilters).length > 0 ? (
              // Filters may be too restrictive — offer a one-tap clear.
              <>
                <div className="w-20 h-20 rounded-full bg-[var(--cream-deep)] border border-[var(--hairline)] flex items-center justify-center mx-auto mb-5">
                  <SlidersHorizontal className="w-8 h-8 text-[#B91C1C]" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-[600] mb-2" data-testid="empty-filters-title">No matches with these filters</h2>
                <p className="text-[#4B4B52] text-sm mb-6">
                  Try loosening your filters — you might be missing a great connection.
                </p>
                <div className="flex flex-col gap-2 items-center">
                  <button
                    onClick={() => { setActiveFilters({}); setFilters(f => ({ ...f, readyOnly: false })); }}
                    data-testid="clear-filters-btn"
                    className="px-6 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold btn-shine"
                  >
                    Clear all filters
                  </button>
                  <button onClick={() => setFilterOpen(true)} className="text-xs text-[#4B4B52] hover:text-[#0F0F10] font-semibold">
                    Adjust filters instead
                  </button>
                </div>
              </>
            ) : (
              // Genuine "no more profiles" — user has swiped through everyone.
              <>
                <div className="w-20 h-20 rounded-full bg-[var(--cream-deep)] border border-[var(--hairline)] flex items-center justify-center mx-auto mb-5">
                  <Heart className="w-8 h-8 text-[#B91C1C]" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-[600] mb-2">No more profiles</h2>
                <p className="text-[#4B4B52] text-sm mb-6">Check back later for new intentional daters.</p>
                <button onClick={fetch} data-testid="refresh-profiles" className="text-sm text-[#B91C1C] font-semibold hover:underline">Refresh</button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="px-3 pt-2">
          {/* Next card preview (stacked under) */}
          {profiles[idx + 1] && (
            <div className="absolute left-3 right-3 pointer-events-none" aria-hidden>
              <div className="rounded-[28px] overflow-hidden opacity-60 scale-[0.95]" style={{height:'calc(100dvh - 400px)', maxHeight:'560px', minHeight:'380px'}}>
                <img src={getPhotoUrl((profiles[idx+1].photos?.[0] || profiles[idx+1].photo_url)) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${profiles[idx+1].user_id}`} alt="" className="w-full h-full object-cover" />
              </div>
            </div>
          )}

          {/* Feb 27 2026 · Phase 166 — Retention-loss card. Auto-hides
              when localStorage.mtk_last_seen_filter_tier ≤ current tier
              OR when the user has dismissed within the cooldown window. */}
          <RetentionCard currentTier={swipeInfo?.tier} />

          {/* Swipable Card — extracted into SwipeCard component (Feb 2026).
              Card is a vertical scroller with the full profile inline;
              action bar lives inside the swipe-transform wrapper so it
              moves/rotates with the card during a drag. */}
          <AnimatePresence mode="wait">
            <SwipeCard
              key={cur.user_id}
              profile={cur}
              swipeInfo={swipeInfo}
              breakdown={breakdown}
              promptsList={promptsList}
              swiping={swiping}
              scrollRef={cardScrollRef}
              onSwipe={swipe}
              onUndo={undo}
              onIntro={() => setMessageOpen(true)}
              onReport={() => setReportOpen(true)}
              onBlock={() => block(cur.user_id)}
              onOpenLightbox={(i) => { setLightboxStartIdx(i); setLightboxOpen(true); }}
            />
          </AnimatePresence>

          {/* Balance indicator */}
          {swipeInfo && swipeInfo.total_seen > 0 && (
            <div className="flex justify-center mt-4">
              <div className="flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white border border-[var(--hairline)]">
                <div className="h-1 w-16 bg-black/[0.06] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all brand-gradient" style={{width:`${Math.min(swipeInfo.right_swipe_percentage||0,100)}%`}} />
                </div>
                <span className="text-[10px] text-[#8A8A93] tracking-wider font-medium">{swipeInfo.right_swipes}/{swipeInfo.total_seen} likes</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Match dialog */}
      <Dialog open={!!matchDialog} onOpenChange={() => setMatchDialog(null)}>
        <DialogContent className="bg-white border-[var(--hairline)] rounded-3xl p-8 text-center max-w-sm overflow-hidden" data-testid="match-dialog">
          <DialogTitle className="sr-only">Match</DialogTitle>
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({length: 20}).map((_, i) => (
              <div key={i} className="absolute animate-float-up" style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}>
                <Heart size={12 + Math.random() * 16} className="text-[#B91C1C]" fill="currentColor" style={{ opacity: 0.15 + Math.random() * 0.3 }} />
              </div>
            ))}
          </div>
          <div className="relative mx-auto mb-5 flex items-center justify-center" style={{ minWidth: 200, minHeight: 112 }}>
            {matchDialog?.celebration?.viewer && matchDialog?.celebration?.other_user ? (
              <>
                <ConfettiBurst
                  count={48}
                  colors={['#B91C1C', '#F59E0B', '#EC4899', '#8B5CF6', '#1E4FD8']}
                  spread={280}
                  duration={1900}
                  data-testid="match-dialog-confetti"
                />
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center">
                  <div className="w-40 h-40 rounded-full bg-[#B91C1C]/10 animate-ping" style={{animationDuration:'1.5s'}} />
                </div>
                <MatchAvatar photoUrl={matchDialog.celebration.viewer.photo_url}
                             name={matchDialog.celebration.viewer.display_name}
                             style={{ marginRight: -22, zIndex: 2 }}
                             className="animate-bounce-in" />
                <MatchAvatar photoUrl={matchDialog.celebration.other_user.photo_url}
                             name={matchDialog.celebration.other_user.display_name}
                             style={{ marginLeft: -22, zIndex: 1, animationDelay: '120ms' }}
                             className="animate-bounce-in" />
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full brand-gradient flex items-center justify-center shadow-md z-10 brand-glow-sm">
                  <Heart className="w-5 h-5 text-white" fill="currentColor" />
                </div>
              </>
            ) : (
              <div className="relative w-24 h-24 flex items-center justify-center">
                <ConfettiBurst count={32}
                               colors={['#B91C1C', '#F59E0B', '#EC4899']}
                               spread={200} duration={1700} />
                <div className="absolute inset-0 rounded-full bg-[#B91C1C]/10 animate-ping" style={{animationDuration:'1.5s'}} />
                <div className="absolute inset-2 rounded-full bg-[#B91C1C]/15 animate-ping" style={{animationDuration:'2s',animationDelay:'0.3s'}} />
                <div className="w-20 h-20 rounded-full brand-gradient flex items-center justify-center animate-bounce-in brand-glow relative z-10">
                  <Heart className="w-10 h-10 text-white" fill="currentColor" />
                </div>
              </div>
            )}
          </div>
          <h2 className="font-display-tight text-4xl font-[600] brand-gradient-text animate-fade-up relative z-10">It's a Match</h2>
          <p className="text-[#4B4B52] mt-2 text-sm animate-fade-up relative z-10" style={{animationDelay:'0.15s'}}>You and <span className="font-semibold text-[#0F0F10]">{matchDialog?.matched_user?.display_name}</span> like each other</p>
          {matchDialog?.compatibility_score && (
            <div className="inline-flex items-center gap-1.5 mt-3 px-4 py-1.5 rounded-full bg-[#B91C1C]/8 border border-[#B91C1C]/15 animate-fade-up relative z-10" style={{animationDelay:'0.3s'}}>
              <Heart size={12} className="text-[#B91C1C]" fill="currentColor" />
              <span className="text-sm font-bold text-[#B91C1C]">{matchDialog.compatibility_score}% compatible</span>
            </div>
          )}
          <div className="flex gap-3 mt-6 animate-fade-up relative z-10" style={{animationDelay:'0.4s'}}>
            <button onClick={() => setMatchDialog(null)} className="flex-1 py-3 rounded-full border border-[var(--hairline-strong)] text-sm font-semibold hover:bg-black/[0.02] transition-colors">Keep Swiping</button>
            <button onClick={() => { setMatchDialog(null); navigate(`/chat/${matchDialog?.match_id}`); }} data-testid="go-to-chat-btn"
              className="btn-shine flex-1 py-3 rounded-full brand-gradient text-white text-sm font-semibold transition-all hover:scale-[1.02] brand-glow-sm">Send Message</button>
          </div>
          <MatchReferralNudge />
        </DialogContent>
      </Dialog>

      {/* Full-screen photo lightbox/carousel */}
      {lightboxOpen && cur && (
        <PhotoLightbox
          photos={photos.map(p => getPhotoUrl(p)).filter(Boolean)}
          startIndex={lightboxStartIdx}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="bg-white border-[var(--hairline)] rounded-2xl p-6 max-w-sm" data-testid="report-dialog">
          <DialogTitle className="font-display text-lg font-[600] mb-4">Report User</DialogTitle>
          <div className="space-y-2">
            {['Inappropriate content','Fake profile','Harassment','Spam','Other'].map(r => (
              <button key={r} onClick={() => report(cur?.user_id, r)} data-testid={`report-reason-${r.toLowerCase().replace(/\s/g,'-')}`}
                className="w-full text-left px-4 py-3 rounded-xl border border-[var(--hairline)] hover:bg-black/[0.02] text-sm transition-colors">{r}</button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Locked-filter paywall (Feb 2026 premium filter system) */}
      <LockedFilterModal
        open={!!lockModal}
        label={lockModal?.label || ''}
        target={lockModal?.target || 'premium'}
        onClose={() => setLockModal(null)}
        onUpgrade={() => {
          const tier = lockModal?.target || 'premium';
          const fk = lockModal?.filter_key;
          trackFilterEvent('paywall_upgrade_clicked', { target_tier: tier, filter_key: fk, tier: swipeInfo?.tier });
          setLockModal(null);
          // Feb 2026 — Route to Subscription screen with structured paywall
          // context so the banner shows exactly which filter drove the tap.
          const reason = tier === 'vip' ? 'filter_vip_locked' : 'filter_premium_locked';
          navigate(`/subscription?feature=filter&reason=${reason}${fk ? `&key=${fk}` : ''}`);
        }}
      />

      {/* Video ad slot — free-tier only, every 20-25 swipes */}
      {adConfig && (
        <VideoAdModal
          open={adOpen}
          config={adConfig}
          onClose={(rewarded) => {
            setAdOpen(false);
            // If reward was granted, refresh swipe info so super_likes_remaining
            // reflects the new purchased inventory server-side.
            if (rewarded) {
              api.get('/discover?refresh=1').then((r) => {
                if (r.data.swipe_info) setSwipeInfo(r.data.swipe_info);
              }).catch(() => {});
            }
          }}
        />
      )}

      {/* Send message dialog (intros) */}
      <SendMessageDialog
        open={messageOpen}
        onOpenChange={setMessageOpen}
        target={cur ? { user_id: cur.user_id, display_name: cur.display_name, photo_url: cur.photo_url } : null}
        onSent={() => {
          // Feb 2026 — intros count as a right-swipe visually. Advance the
          // deck so we don't show the same profile twice and match the
          // Rose / Like animation the user just triggered.
          setTimeout(() => {
            setIdx(i => i + 1);
            maybeShowAd();
          }, 200);
        }}
      />

      {/* Filter dialog */}
      <Dialog open={filterOpen} onOpenChange={(o) => {
        setFilterOpen(o);
        // Refresh saved searches when the drawer opens so multi-device edits
        // (e.g., a rename made on mobile) show up on the web without a reload.
        if (o) { loadSavedSearches(); }
      }}>
        <DialogContent className="bg-white border-[var(--hairline)] rounded-2xl p-6 max-w-md max-h-[90vh] overflow-y-auto" data-testid="filter-dialog">
          <DialogTitle className="font-display text-xl font-[600] mb-1">Discovery Filters</DialogTitle>
          <p className="text-xs text-[#8A8A93] mb-5">Be intentional — narrow to who you're really looking for.</p>

          {/* ==================== Saved Searches (Phase 2b) ==================== */}
          <div className="mb-5 rounded-xl border border-[var(--hairline)] bg-[#FAFAFA] p-3" data-testid="saved-searches-block">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-display text-[12px] uppercase tracking-[0.14em] font-[700] text-[#0F0F10]">Saved Searches</span>
                <span className="text-[10px] text-[#8A8A93]" data-testid="saved-searches-quota">
                  {savedQuota.used}/{savedQuota.quota === 999 ? '∞' : savedQuota.quota}
                </span>
              </div>
              {!savingSearch && (
                <button
                  type="button"
                  onClick={() => {
                    if (savedQuota.used >= savedQuota.quota) {
                      openLockPaywall({ label: 'Saved searches', target: savedQuota.tier === 'free' ? 'premium' : 'vip', filter_key: 'savedSearches' });
                      return;
                    }
                    setSavingSearch(true);
                  }}
                  className="text-[11px] font-semibold text-[#B91C1C] hover:underline"
                  data-testid="save-current-btn"
                >
                  Save current
                </button>
              )}
            </div>
            {savingSearch && (
              <div className="flex gap-2 mb-2" data-testid="save-current-form">
                <input
                  type="text"
                  value={newSearchName}
                  onChange={e => setNewSearchName(e.target.value)}
                  placeholder='Name it (e.g. NYC 30s Vegan)'
                  maxLength={40}
                  className="flex-1 px-3 py-2 rounded-lg border border-[var(--hairline-strong)] text-sm focus:outline-none focus:border-[#B91C1C]"
                  data-testid="save-current-name"
                  autoFocus
                />
                <button onClick={saveCurrentAsSearch} className="px-3 py-2 rounded-lg brand-gradient text-white text-xs font-semibold" data-testid="save-current-confirm">Save</button>
                <button onClick={() => { setSavingSearch(false); setNewSearchName(''); }} className="px-2 py-2 rounded-lg text-[#8A8A93] text-xs" data-testid="save-current-cancel">Cancel</button>
              </div>
            )}
            {savedSearches.length === 0 && !savingSearch && (
              <p className="text-[11px] text-[#8A8A93] italic" data-testid="saved-searches-empty">Save your current filter combo to reload it later in one tap.</p>
            )}
            {savedSearches.length > 0 && (
              <div className="space-y-1.5" data-testid="saved-searches-list">
                {savedSearches.map(s => (
                  <div key={s.search_id} className="flex items-center gap-2 py-1" data-testid={`saved-search-row-${s.search_id}`}>
                    {editingSearchId === s.search_id ? (
                      <>
                        <input
                          value={editingSearchName}
                          onChange={e => setEditingSearchName(e.target.value)}
                          maxLength={40}
                          onKeyDown={e => { if (e.key === 'Enter') renameSavedSearch(s); if (e.key === 'Escape') setEditingSearchId(null); }}
                          className="flex-1 px-2 py-1 rounded-md border border-[var(--hairline-strong)] text-sm"
                          data-testid={`saved-search-rename-input-${s.search_id}`}
                          autoFocus
                        />
                        <button onClick={() => renameSavedSearch(s)} className="text-[11px] font-semibold text-[#B91C1C]" data-testid={`saved-search-rename-save-${s.search_id}`}>Save</button>
                        <button onClick={() => setEditingSearchId(null)} className="text-[11px] text-[#8A8A93]">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => applySavedSearch(s)}
                          className="flex-1 text-left px-2 py-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-sm text-[#0F0F10] font-medium truncate"
                          data-testid={`saved-search-load-${s.search_id}`}
                        >
                          {s.is_default && <span className="inline-block mr-1.5 text-[9px] font-bold text-[#B45309] bg-[#FEF3C7] px-1.5 py-0.5 rounded-full uppercase tracking-wider" data-testid={`saved-search-default-badge-${s.search_id}`}>Default</span>}
                          {s.name}
                        </button>
                        <button
                          onClick={() => setDefaultSavedSearch(s)}
                          title={s.is_default ? 'Remove default' : 'Set as default'}
                          className={`text-xs px-1.5 py-1 rounded transition-colors ${s.is_default ? 'text-[#B45309]' : 'text-[#8A8A93] hover:text-[#B45309]'}`}
                          data-testid={`saved-search-default-toggle-${s.search_id}`}
                        >
                          <Star size={13} fill={s.is_default ? 'currentColor' : 'none'} strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => { setEditingSearchId(s.search_id); setEditingSearchName(s.name); }}
                          className="text-[11px] text-[#8A8A93] hover:text-[#0F0F10] px-1"
                          data-testid={`saved-search-rename-${s.search_id}`}
                        >Rename</button>
                        <button
                          onClick={() => deleteSavedSearch(s)}
                          className="text-[11px] text-[#8A8A93] hover:text-[#B91C1C] px-1"
                          data-testid={`saved-search-delete-${s.search_id}`}
                        >Delete</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1">
            {/* ==================== WHAT YOU NEED — FREE (Phase 163) ==================== */}
            <TierGroup
              title="What You Need"
              subtitle="The essentials that matter most."
              testId="tier-group-what-you-need">
            {/* ==================== 1. Basics ==================== */}
            <FilterSection title="Basics" testId="filter-section-basics">
              <div>
                <Label className="text-sm text-[#4B4B52] mb-2 block">Show me</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="filter-show-me">
                  {[['default','Default'],['male','Men'],['female','Women']].map(([v,l]) => (
                    <button key={v} onClick={() => setFilters(f => ({...f, showMe: v}))} type="button"
                      data-testid={`filter-show-${v}`}
                      className={`py-2.5 rounded-xl text-sm font-semibold transition-all border ${
                        filters.showMe === v
                          ? 'brand-gradient text-white border-transparent'
                          : 'bg-white text-[#0F0F10] border-[var(--hairline-strong)] hover:bg-black/[0.02]'
                      }`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm text-[#4B4B52] mb-2 block">Age range: <span className="text-[#B91C1C] font-semibold">{filters.minAge} – {filters.maxAge}</span></Label>
                <div className="flex gap-4 items-center">
                  <span className="text-xs text-[#8A8A93] w-6">{filters.minAge}</span>
                  <Slider value={[filters.minAge, filters.maxAge]} onValueChange={v => setFilters(f => ({...f, minAge: v[0], maxAge: v[1]}))}
                    min={18} max={99} step={1} data-testid="filter-age-range" className="flex-1" />
                  <span className="text-xs text-[#8A8A93] w-6">{filters.maxAge}</span>
                </div>
              </div>
              <div>
                <Label className="text-sm text-[#4B4B52] mb-2 block">Max distance: <span className="text-[#B91C1C] font-semibold">{kmToDisplay(filters.maxDistance)} {distanceLabel()}</span></Label>
                <Slider value={[kmToDisplay(filters.maxDistance)]} onValueChange={v => setFilters(f => ({...f, maxDistance: displayToKm(v[0])}))}
                  min={getUnit() === 'metric' ? 5 : 3}
                  max={getUnit() === 'metric' ? 200 : 125}
                  step={getUnit() === 'metric' ? 5 : 2}
                  data-testid="filter-distance" />
                <div className="flex justify-between text-xs text-[#8A8A93] mt-1">
                  <span>{getUnit() === 'metric' ? '5 km' : '3 mi'}</span>
                  <span>{getUnit() === 'metric' ? '200 km' : '125 mi'}</span>
                </div>
              </div>
              <FilterSelect label="Sexual orientation" value={filters.sexualOrientation} testId="filter-orientation" filterKey="sexualOrientation" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, sexualOrientation: v}))}
                options={[['','Any'],['straight','Straight'],['gay','Gay'],['lesbian','Lesbian'],['bisexual','Bisexual'],['pansexual','Pansexual'],['asexual','Asexual'],['queer','Queer']]} />
            </FilterSection>

            {/* ==================== 2. Relationships & Family ==================== */}
            <FilterSection title="Relationships & Family" testId="filter-section-relationships">
              <FilterSelect label="Relationship goal" value={filters.relationshipGoal} testId="filter-goal" filterKey="relationshipGoal" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, relationshipGoal: v}))}
                options={[
                  ['','Any'],
                  ['casual','Casual dating'],
                  ['serious','Serious relationship'],
                  ['long_term','Long-term'],
                  ['marriage','Marriage'],
                  ['life_partner','Life partner'],
                  ['friendship','Just meeting people'],
                  ['taking_it_slow','Taking it slow'],
                  ['open','Open to either'],
                ]} />
              <FilterSelect label="Have kids" value={filters.has_kids} testId="filter-has-kids" filterKey="has_kids" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, has_kids: v}))}
                options={[['','Any'],['yes','Yes'],['no','No'],['prefer_not_say','Prefer not to say']]} />
              <FilterSelect label="Want kids" value={filters.wants_kids} testId="filter-wants-kids" filterKey="wants_kids" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, wants_kids: v}))}
                options={[['','Any'],['yes','Yes'],['no','No'],['someday','Someday'],['not_sure','Not sure']]} />
              <FilterSelect label="Monogamy" value={filters.monogamy} testId="filter-monogamy" filterKey="monogamy" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, monogamy: v}))}
                options={[['','Any'],['monogamous','Monogamous'],['non_monogamous','Non-monogamous'],['open','Open to either']]} />
              <FilterSelect label="Current status" value={filters.marital_status} testId="filter-marital-status" filterKey="marital_status" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, marital_status: v}))}
                options={[['','Any'],['single','Single'],['separated','Separated'],['divorced','Divorced'],['widowed','Widowed'],['its_complicated',"It's complicated"]]} />
              <FilterSelect label="Ever been married?" value={filters.marital_history} testId="filter-marital-history" filterKey="marital_history" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, marital_history: v}))}
                options={[['','Any'],['never_married','Never married'],['divorced','Divorced'],['separated','Separated'],['widowed','Widowed']]} />
              <FilterSelect label="Willing to relocate" value={filters.willing_to_relocate} testId="filter-willing-to-relocate" filterKey="willing_to_relocate" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, willing_to_relocate: v}))}
                options={[['','Any'],['local_only','Local only'],['open_to_ldr','Open to long-distance'],['open_to_relocating','Open to relocating']]} />
            </FilterSection>

            {/* ==================== 3. Core Lifestyle Dealbreakers (FREE) ====================
                Feb 27 2026 · Phase 163 — Drinking + Smoking pulled out of the
                broader "Lifestyle" section and given their own dealbreaker
                bucket at the free tier per the reorganization spec. */}
            <FilterSection title="Core Lifestyle Dealbreakers" testId="filter-section-core-lifestyle">
              <FilterSelect label="Drinking" value={filters.drinking} testId="filter-drinking" filterKey="drinking" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, drinking: v}))}
                options={[['','Any'],['never','Never'],['rarely','Rarely'],['socially','Socially'],['often','Often']]} />
              <FilterSelect label="Smoking" value={filters.smoking} testId="filter-smoking" filterKey="smoking" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, smoking: v}))}
                options={[['','Any'],['never','Never'],['socially','Socially'],['sometimes','Sometimes'],['often','Often'],['formerly','Formerly']]} />
            </FilterSection>

            </TierGroup>

            {/* ==================== YOUR TYPE — PREMIUM (Phase 163) ==================== */}
            <TierGroup
              title="Your Type"
              subtitle="Get more specific about who you're looking for."
              lockedTier={tierRank(swipeInfo?.tier) < 1 ? 'premium' : null}
              celebrating={upgradeCelebration === 'premium'}
              testId="tier-group-your-type">

            {/* ==================== 4. Lifestyle (Premium) ==================== */}
            <FilterSection title="Lifestyle" lockedTier={tierRank(swipeInfo?.tier) < 1 ? 'premium' : null} testId="filter-section-lifestyle">
              {/* Feb 2026 — Cannabis / Marijuana filter (Premium). Same 5-value
                  scale as smoking so the two are directly comparable. */}
              <FilterSelect label="Marijuana" value={filters.cannabis} testId="filter-cannabis" filterKey="cannabis" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, cannabis: v}))}
                options={[['','Any'],['never','Never'],['socially','Socially'],['sometimes','Sometimes'],['often','Often'],['formerly','Formerly']]} />
              {/* Feb 2026 — Recreational drugs filter (Premium). Separate from
                  marijuana because "never for weed" doesn't imply "never for
                  other drugs" — often a serious-dating dealbreaker signal. */}
              <FilterSelect label="Recreational drugs" value={filters.drugs} testId="filter-drugs" filterKey="drugs" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, drugs: v}))}
                options={[['','Any'],['never','Never'],['socially','Socially'],['sometimes','Sometimes'],['prefer_not_say','Prefer not to say']]} />
              <FilterSelect label="Exercise" value={filters.exercise} testId="filter-exercise" filterKey="exercise" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, exercise: v}))}
                options={[['','Any'],['daily','Daily'],['often','Often'],['sometimes','Sometimes'],['rarely','Rarely'],['never','Never']]} />
              <FilterMulti label="Diet" values={filters.diet} testId="filter-diet" filterKey="diet" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={vs => setFilters(f => ({...f, diet: vs}))}
                options={[['','Any'],['Omnivore','Omnivore'],['Vegetarian','Vegetarian'],['Vegan','Vegan'],['Pescatarian','Pescatarian'],['Kosher','Kosher'],['Halal','Halal']]} />
              <FilterSelect label="Pets" value={filters.pets} testId="filter-pets" filterKey="pets" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, pets: v}))}
                options={[['','Any'],['Dog','Dog'],['Cat','Cat'],['Both','Both'],['None','None'],['Other','Other']]} />
              {/* Feb 2026 — Sleep schedule filter (Premium). Simple 3-value
                  rhythm signal — surprisingly load-bearing for intent-oriented daters. */}
              <FilterSelect label="Sleep schedule" value={filters.sleep_schedule} testId="filter-sleep-schedule" filterKey="sleep_schedule" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, sleep_schedule: v}))}
                options={[['','Any'],['early_bird','Early bird'],['night_owl','Night owl'],['somewhere_in_between','Somewhere in between']]} />
              <div>
                <Label className="text-sm text-[#4B4B52] mb-2 flex items-center gap-1.5">
                  Height
                  {tierRank(swipeInfo?.tier) < 1 && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C]/80 bg-[#B91C1C]/10 px-1.5 py-0.5 rounded-full" data-testid="filter-lock-minHeight">
                      <Lock size={9} />
                      Premium
                    </span>
                  )}
                  <span className="ml-auto text-[#B91C1C] font-semibold">{formatHeight(filters.minHeight)} – {formatHeight(filters.maxHeight)}</span>
                </Label>
                <div className={tierRank(swipeInfo?.tier) < 1 ? 'opacity-50 pointer-events-none' : ''}>
                  <Slider value={[filters.minHeight, filters.maxHeight]}
                    onValueChange={v => setFilters(f => ({...f, minHeight: v[0], maxHeight: v[1]}))}
                    min={140} max={220} step={1} data-testid="filter-height" disabled={tierRank(swipeInfo?.tier) < 1} />
                  <div className="flex justify-between text-xs text-[#8A8A93] mt-1">
                    <span>{formatHeight(140)}</span>
                    <span>{formatHeight(220)}</span>
                  </div>
                </div>
                {tierRank(swipeInfo?.tier) < 1 && (
                  <button type="button" onClick={() => openLockPaywall({ label: 'Height', target: 'premium', filter_key: 'minHeight' })} className="mt-1 text-[11px] text-[#B91C1C] font-semibold" data-testid="filter-height-locked-cta">Unlock height range</button>
                )}
              </div>
              <FilterMulti label="Body type" values={filters.bodyType} testId="filter-body-type" filterKey="bodyType" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={vs => setFilters(f => ({...f, bodyType: vs}))}
                options={[['','Any'],['Slim','Slim'],['Athletic','Athletic'],['Average','Average'],['Stocky','Stocky'],['Curvy','Curvy'],['Plus-size','Plus-size'],['Muscular','Muscular']]} />
            </FilterSection>

            {/* ==================== 4. Background (Premium) ==================== */}
            <FilterSection title="Background" lockedTier={tierRank(swipeInfo?.tier) < 1 ? 'premium' : null} testId="filter-section-background">
              <FilterSelect label="Religion" value={filters.religion} testId="filter-religion" filterKey="religion" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, religion: v}))}
                options={[['','Any'], ...religionsCatalog.map(r => [r.value, r.label])]} />
              <FilterSelect label="Education" value={filters.education} testId="filter-education" filterKey="education" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, education: v}))}
                options={[['','Any'], ...educationCatalog.map(e => [e.value, e.label])]} />
              <FilterSelect label="Industry" value={filters.industry} testId="filter-industry" filterKey="industry" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, industry: v}))}
                options={[['','Any'], ...industriesCatalog.map(i => [i, i])]} />
              <FilterMulti label="Ethnicity / Race" values={filters.race} testId="filter-race" filterKey="race" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={vs => setFilters(f => ({...f, race: vs}))}
                options={[['','Any'],['Asian','Asian'],['Black/African Descent','Black'],['Hispanic/Latino','Hispanic/Latino'],['Middle Eastern','Middle Eastern'],['Native American','Native American'],['Pacific Islander','Pacific Islander'],['White/Caucasian','White'],['Mixed','Mixed'],['Other','Other']]} />
              <FilterSelect label="Speaks language" value={filters.language} testId="filter-language" filterKey="language" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, language: v}))}
                options={[['','Any'], ...languagesCatalog.map(l => [l, l])]} />
              <FilterMulti label="Politics" values={filters.politics} testId="filter-politics" filterKey="politics" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={vs => setFilters(f => ({...f, politics: vs}))}
                options={[['','Any'], ...politicsCatalog.map(p => [p, p])]} />
            </FilterSection>

            </TierGroup>

            {/* ==================== YOUR BEST MATCHES — VIP (Phase 163) ==================== */}
            <TierGroup
              title="Your Best Matches"
              subtitle="Find the people most likely to be right for you."
              lockedTier={tierRank(swipeInfo?.tier) < 2 ? 'vip' : null}
              celebrating={upgradeCelebration === 'vip'}
              testId="tier-group-your-best-matches">

            {/* ==================== 5. Compatibility (VIP) ==================== */}
            <FilterSection title="Compatibility" defaultOpen={false} lockedTier={tierRank(swipeInfo?.tier) < 2 ? 'vip' : null} testId="filter-section-compatibility">
              <div>
                <Label className="text-sm text-[#4B4B52] mb-2 flex items-center gap-1.5">
                  Minimum compatibility
                  {tierRank(swipeInfo?.tier) < 2 && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C]/80 bg-[#B91C1C]/10 px-1.5 py-0.5 rounded-full" data-testid="filter-lock-minCompatibility">
                      <Lock size={9} />
                      VIP
                    </span>
                  )}
                  <span className="ml-auto text-[#B91C1C] font-semibold">{filters.minCompatibility}%</span>
                </Label>
                <div className={tierRank(swipeInfo?.tier) < 2 ? 'opacity-50 pointer-events-none' : ''}>
                  <Slider value={[filters.minCompatibility]} onValueChange={v => setFilters(f => ({...f, minCompatibility: v[0]}))}
                    min={0} max={95} step={5} data-testid="filter-min-compat" disabled={tierRank(swipeInfo?.tier) < 2} />
                </div>
                {tierRank(swipeInfo?.tier) < 2 && (
                  <button type="button" onClick={() => openLockPaywall({ label: 'Minimum compatibility', target: 'vip', filter_key: 'minCompatibility' })} className="mt-1 text-[11px] text-[#B91C1C] font-semibold" data-testid="filter-min-compat-locked-cta">Unlock compatibility floor</button>
                )}
              </div>
              <FilterSelect label="Personality" value={filters.personalityType} testId="filter-personality" filterKey="personalityType" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, personalityType: v}))}
                options={[['','Any'], ...personalityCatalog.map(p => [p, p])]} />
              <FilterSelect label="Interest" value={filters.interest} testId="filter-interest" filterKey="interest" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, interest: v}))}
                searchable={true}
                options={[['','Any'], ...interestsCatalog.map(i => [i, i])]} />
              <FilterSelect label="Zodiac" value={filters.zodiacSign} testId="filter-zodiac" filterKey="zodiacSign" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, zodiacSign: v}))}
                options={[['','Any'],['Aries','Aries'],['Taurus','Taurus'],['Gemini','Gemini'],['Cancer','Cancer'],['Leo','Leo'],['Virgo','Virgo'],['Libra','Libra'],['Scorpio','Scorpio'],['Sagittarius','Sagittarius'],['Capricorn','Capricorn'],['Aquarius','Aquarius'],['Pisces','Pisces']]} />
              {/* Feb 2026 — VIP compat-pillar filters. Same source of truth
                  as the compat scorer (`profile.answers.<key>`). Option values
                  MUST match COMPATIBILITY_QUESTIONS in backend/routes/compatibility.py. */}
              <FilterSelect label="Love language" value={filters.loveLanguage} testId="filter-love-language" filterKey="loveLanguage" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, loveLanguage: v}))}
                options={[['','Any'],['words_of_affirmation','Words of affirmation'],['quality_time','Quality time'],['physical_touch','Physical touch'],['acts_of_service','Acts of service'],['gifts','Thoughtful gifts']]} />
              <FilterSelect label="Communication style" value={filters.communicationStyle} testId="filter-communication-style" filterKey="communicationStyle" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, communicationStyle: v}))}
                options={[['','Any'],['talk_it_out','Talks it out'],['need_space','Needs space first'],['written','Writes it out'],['avoid','Avoids confrontation']]} />
              <FilterSelect label="Family plans" value={filters.familyPlans} testId="filter-family-plans" filterKey="familyPlans" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, familyPlans: v}))}
                options={[['','Any'],['yes','Wants kids'],['open_to_it','Open to kids'],['no','Doesn\'t want kids'],['have_children','Already has kids']]} />
              <FilterSelect label="Social energy" value={filters.socialEnergy} testId="filter-social-energy" filterKey="socialEnergy" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, socialEnergy: v}))}
                options={[['','Any'],['introvert','Introvert'],['extrovert','Extrovert'],['ambivert','Ambivert']]} />
            </FilterSection>

            {/* ==================== 6. Availability & Activity ==================== */}
            <FilterSection title="Availability & Activity" testId="filter-section-availability">
              <FilterSelect label="Open to meeting this week?" value={filters.meetingReadiness} testId="filter-meeting-readiness" filterKey="meetingReadiness" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, meetingReadiness: v}))}
                options={[['','Any'],['yes','Yes'],['maybe','Maybe'],['not_now','Not right now']]} />
              <FilterSelect label="Preferred date type" value={filters.dateType} testId="filter-date-type" filterKey="dateType" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, dateType: v}))}
                options={[['','Any'],['drinks','Drinks'],['food','Food'],['activities','Activities'],['walks','Walks']]} />
              <div className="flex items-center justify-between py-2 border-y border-[var(--hairline)]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" />
                  <Label className="text-sm text-[#0F0F10] font-medium">Online now only</Label>
                  {tierRank(swipeInfo?.tier) < 2 && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C]/80 bg-[#B91C1C]/10 px-1.5 py-0.5 rounded-full" data-testid="filter-lock-onlineOnly">
                      <Lock size={9} />VIP
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (tierRank(swipeInfo?.tier) < 2) {
                      openLockPaywall({ label: 'Online now only', target: 'vip', filter_key: 'onlineOnly' });
                      return;
                    }
                    setFilters(f => ({...f, onlineOnly: !f.onlineOnly}));
                  }}
                  data-testid="filter-online-only"
                  className={`relative w-10 h-6 rounded-full transition-colors ${filters.onlineOnly ? 'bg-[#16A34A]' : 'bg-black/[0.08]'} ${tierRank(swipeInfo?.tier) < 2 ? 'opacity-60' : ''}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${filters.onlineOnly ? 'translate-x-4' : ''}`} />
                </button>
              </div>
              <FilterSelect label="Recently active" value={filters.active_recency} testId="filter-active-recency" filterKey="active_recency" currentTier={swipeInfo?.tier} onLocked={openLockPaywall}
                onChange={v => setFilters(f => ({...f, active_recency: v}))}
                options={[['','Any'],['today','Active today'],['this_week','Active this week']]} />
            </FilterSection>

            {/* ==================== 7. Trust ==================== */}
            <FilterSection title="Trust" testId="filter-section-trust">
              <div className="flex items-center justify-between py-2 border-y border-[var(--hairline)]">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#3B82F6]"/>
                  <Label className="text-sm text-[#0F0F10] font-medium">Verified profiles only</Label>
                  {tierRank(swipeInfo?.tier) < 2 && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C]/80 bg-[#B91C1C]/10 px-1.5 py-0.5 rounded-full" data-testid="filter-lock-verifiedOnly">
                      <Lock size={9} />VIP
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (tierRank(swipeInfo?.tier) < 2) {
                      openLockPaywall({ label: 'Verified profiles only', target: 'vip', filter_key: 'verifiedOnly' });
                      return;
                    }
                    setFilters(f => ({...f, verifiedOnly: !f.verifiedOnly}));
                  }}
                  data-testid="filter-verified"
                  className={`relative w-10 h-6 rounded-full transition-colors ${filters.verifiedOnly ? 'bg-[#B91C1C]' : 'bg-black/[0.08]'} ${tierRank(swipeInfo?.tier) < 2 ? 'opacity-60' : ''}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${filters.verifiedOnly ? 'translate-x-4' : ''}`} />
                </button>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[var(--hairline)]">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#0F0F10]"/>
                  <Label className="text-sm text-[#0F0F10] font-medium">Complete profiles only</Label>
                  {tierRank(swipeInfo?.tier) < 1 && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C]/80 bg-[#B91C1C]/10 px-1.5 py-0.5 rounded-full" data-testid="filter-lock-completeProfilesOnly">
                      <Lock size={9} />Premium
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (tierRank(swipeInfo?.tier) < 1) {
                      openLockPaywall({ label: 'Complete profiles only', target: 'premium', filter_key: 'complete_profiles_only' });
                      return;
                    }
                    setFilters(f => ({...f, complete_profiles_only: !f.complete_profiles_only}));
                  }}
                  data-testid="filter-complete-profiles"
                  className={`relative w-10 h-6 rounded-full transition-colors ${filters.complete_profiles_only ? 'bg-[#B91C1C]' : 'bg-black/[0.08]'} ${tierRank(swipeInfo?.tier) < 1 ? 'opacity-60' : ''}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${filters.complete_profiles_only ? 'translate-x-4' : ''}`} />
                </button>
              </div>
              {/* Feb 27 2026 · Phase 164 — New Members (VIP). Surfaces profiles
                  created within the last 14 days. Server-wired to filter on
                  `profile.created_at` in /discover. */}
              <div className="flex items-center justify-between py-2 border-b border-[var(--hairline)]">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-[#B91C1C]" />
                  <Label className="text-sm text-[#0F0F10] font-medium">New members</Label>
                  {tierRank(swipeInfo?.tier) < 2 && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C]/80 bg-[#B91C1C]/10 px-1.5 py-0.5 rounded-full" data-testid="filter-lock-new_members">
                      <Lock size={9} />VIP
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (tierRank(swipeInfo?.tier) < 2) {
                      openLockPaywall({ label: 'New members', target: 'vip', filter_key: 'new_members' });
                      return;
                    }
                    setFilters(f => ({...f, new_members: !f.new_members}));
                  }}
                  data-testid="filter-new-members"
                  className={`relative w-10 h-6 rounded-full transition-colors ${filters.new_members ? 'bg-[#B91C1C]' : 'bg-black/[0.08]'} ${tierRank(swipeInfo?.tier) < 2 ? 'opacity-60' : ''}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${filters.new_members ? 'translate-x-4' : ''}`} />
                </button>
              </div>
              {/* Feb 27 2026 · Phase 164 — Hide Inactive (VIP). Excludes
                  profiles idle 30+ days via `profile.last_active_at`. */}
              <div className="flex items-center justify-between py-2 border-b border-[var(--hairline)]">
                <div className="flex items-center gap-2">
                  <Activity size={16} className="text-[#8A8A93]" />
                  <Label className="text-sm text-[#0F0F10] font-medium">Hide inactive profiles</Label>
                  {tierRank(swipeInfo?.tier) < 2 && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C]/80 bg-[#B91C1C]/10 px-1.5 py-0.5 rounded-full" data-testid="filter-lock-hide_inactive">
                      <Lock size={9} />VIP
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (tierRank(swipeInfo?.tier) < 2) {
                      openLockPaywall({ label: 'Hide inactive profiles', target: 'vip', filter_key: 'hide_inactive' });
                      return;
                    }
                    setFilters(f => ({...f, hide_inactive: !f.hide_inactive}));
                  }}
                  data-testid="filter-hide-inactive"
                  className={`relative w-10 h-6 rounded-full transition-colors ${filters.hide_inactive ? 'bg-[#B91C1C]' : 'bg-black/[0.08]'} ${tierRank(swipeInfo?.tier) < 2 ? 'opacity-60' : ''}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${filters.hide_inactive ? 'translate-x-4' : ''}`} />
                </button>
              </div>
            </FilterSection>

            </TierGroup>

            <div className="flex gap-3 pt-4 sticky bottom-0 bg-white">
              <button
                onClick={() => { setActiveFilters({}); setFilters(DEFAULT_FILTERS); setFilterOpen(false); }}
                className="flex-1 py-3 rounded-full border border-[var(--hairline-strong)] text-sm font-medium hover:bg-black/[0.02]" data-testid="clear-filters">
                Clear All
              </button>
              <button
                onClick={() => {
                  setActiveFilters(filters);
                  // Analytics — which non-default filters did they apply?
                  const applied = Object.entries(filters)
                    .filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(DEFAULT_FILTERS[k]))
                    .map(([k]) => k);
                  trackFilterEvent('filter_applied', { filter_keys: applied, tier: swipeInfo?.tier });
                  // Feb 2026 · v2.3.0 — persist physical prefs (height range,
                  // body type) into `meeting_prefs` so the compat scorer's
                  // dating_eligibility pillar sees them. Fire-and-forget;
                  // failure to persist doesn't block Discover from applying
                  // the client-side filter.
                  const persistPrefs = {};
                  if (filters.minHeight !== DEFAULT_FILTERS.minHeight) persistPrefs.preferred_height_min = filters.minHeight;
                  if (filters.maxHeight !== DEFAULT_FILTERS.maxHeight) persistPrefs.preferred_height_max = filters.maxHeight;
                  // Feb 2026 · Phase 137 — bodyType is now a multi-select
                  // array. Preserve full user preference by lowercasing each
                  // value (matches scorer's case-insensitive `_fits_body_type`).
                  if (Array.isArray(filters.bodyType) && filters.bodyType.length) {
                    persistPrefs.preferred_body_types = filters.bodyType.map(v => String(v).toLowerCase());
                  }
                  if (Object.keys(persistPrefs).length > 0) {
                    api.put('/profile', { meeting_prefs: persistPrefs }).catch(() => {});
                  }
                  setFilterOpen(false);
                }}
                data-testid="apply-filters"
                className="btn-shine flex-1 py-3 rounded-full brand-gradient text-white text-sm font-semibold brand-glow-sm">
                Apply Filters
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * QuotaChip — persistent daily/weekly allowance chip shown at the top of Discover.
 *
 * Priority (highest signal first):
 *   1. Active boost countdown            → "⚡ Boosted · 24m left"
 *   2. Super Likes remaining > 0         → "⭐ 1 Super Like today"
 *   3. Super Likes = 0 (all tiers)       → "⭐ Out of Super Likes · Get more"
 *   4. Free tier & > 0 remaining         → "⭐ 1 Super Like today · Upgrade for more"
 *
 * Tap behavior:
 *   • Boost chip → navigate to Store (boost pack)
 *   • Super Like chip when 0 → navigate to Store (super like pack) — hard upsell
 *   • Super Like chip when > 0 → no-op (informational)
 */
function QuotaChip({ swipeInfo, boostStatus, onSuperLikeUpsell, onBoostBuyMore }) {
  // Priority 1: active boost
  if (boostStatus?.active && boostStatus.seconds_remaining > 0) {
    const mins = Math.max(1, Math.ceil(boostStatus.seconds_remaining / 60));
    const label = boostStatus.seconds_remaining < 60 ? '<1 min left' : `${mins} min left`;
    return (
      <button
        onClick={onBoostBuyMore}
        data-testid="quota-chip-boost"
        className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] transition-transform hover:scale-[1.02] active:scale-95
                   bg-gradient-to-r from-[#B91C1C] to-[#7F1D1D] text-white shadow-[0_2px_10px_-2px_rgba(185,28,28,0.55)] border border-white/10"
      >
        <Zap size={12} strokeWidth={2.4} fill="currentColor" />
        <span data-testid="boost-countdown">Boosted · {label}</span>
      </button>
    );
  }

  // No swipe info yet — render nothing (loading state above the chip)
  if (!swipeInfo) return null;

  const remaining = swipeInfo.super_likes_remaining ?? 0;
  const tier = swipeInfo.tier || 'free';

  // Priority 2: out of super likes — hard upsell
  if (remaining <= 0) {
    return (
      <button
        onClick={onSuperLikeUpsell}
        data-testid="quota-chip-super-out"
        className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] transition-transform hover:scale-[1.02] active:scale-95
                   bg-white text-[#4B4B52] border border-[var(--hairline-strong)] hover:border-[#B91C1C]/40 hover:text-[#B91C1C]"
      >
        <Star size={12} strokeWidth={2.4} />
        <span>Out of Super Likes · Get more</span>
      </button>
    );
  }

  // Priority 3: have super likes to spend
  const plural = remaining === 1 ? 'Super Like' : 'Super Likes';
  const secondary = tier === 'free' ? ' · Upgrade for 5/day' : '';
  return (
    <button
      onClick={tier === 'free' ? onSuperLikeUpsell : undefined}
      data-testid="quota-chip-super"
      disabled={tier !== 'free'}
      className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] transition-transform border
                 ${tier === 'free'
                   ? 'bg-white text-[#0F0F10] border-[var(--hairline-strong)] hover:border-[#B91C1C]/40 hover:scale-[1.02] active:scale-95 cursor-pointer'
                   : 'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A] cursor-default'
                 }`}
    >
      <Star size={12} strokeWidth={2.4} fill="currentColor" className={tier !== 'free' ? 'text-[#F59E0B]' : ''} />
      <span>{remaining} {plural} today{secondary}</span>
    </button>
  );
}


/**
 * FilterTierMap — which subscription tier is required for each filter key.
 * Keys correspond to `filters.<key>` in the component's state. Missing keys
 * default to 'free'. Backend has its own copy of this map in
 * routes/discover.py — the two MUST stay in sync.
 */
const FILTER_TIER_MAP = {
  // free — What You Need · dealbreakers that matter most
  minAge: 'free', maxAge: 'free', maxDistance: 'free', showMe: 'free',
  relationshipGoal: 'free',  // Feb 2026 — `intent` removed from UI (options merged here)
  // Kids — split into two distinct filters (Feb 2026). Both free-tier since
  // the underlying backend params (has_kids/wants_kids) are also free.
  has_kids: 'free', wants_kids: 'free',
  sexualOrientation: 'free',
  // Feb 27 2026 · Phase 163 — moved to FREE per the "dealbreakers" tier:
  //   • drinking, smoking      — Core Lifestyle Dealbreakers
  //   • monogamy               — Relationship style (fundamental compat requirement)
  drinking: 'free', smoking: 'free', monogamy: 'free',
  // premium — Your Type · lifestyle + demographic preferences
  religion: 'premium', education: 'premium', minHeight: 'premium', maxHeight: 'premium',
  exercise: 'premium', politics: 'premium', zodiacSign: 'premium', language: 'premium',
  race: 'premium', ethnicity: 'premium', industry: 'premium', personalityType: 'premium',
  interest: 'premium',  // Feb 2026 — same tier as personality (VIP compat filters)
  bodyType: 'premium', diet: 'premium', pets: 'premium',
  dateType: 'premium', meetingReadiness: 'premium',
  marijuana: 'premium', drugs: 'premium',
  cannabis: 'premium', marital_history: 'premium', marital_status: 'premium',
  sleep_schedule: 'premium',
  willing_to_relocate: 'premium',
  complete_profiles_only: 'premium',
  // Feb 27 2026 · Phase 164 — VIP filters wired to real backend data.
  new_members: 'vip',
  hide_inactive: 'vip',
  // vip — Your Best Matches · proprietary + high-signal filters
  minCompatibility: 'vip', verifiedOnly: 'vip', onlineOnly: 'vip', readyOnly: 'vip',
  compatibilityLoveLang: 'vip', compatibilityValues: 'vip', compatibilityFamilyGoals: 'vip',
  // Feb 2026 — VIP compat-pillar filters (match against profile.answers.<key>).
  loveLanguage: 'vip', communicationStyle: 'vip', familyPlans: 'vip', socialEnergy: 'vip',
  // Feb 27 2026 · Phase 163 — moved from PREMIUM to VIP per "Best Matches" tier.
  active_recency: 'vip',
};

function tierRank(t) { return { free: 0, premium: 1, vip: 2 }[t] ?? 0; }

/**
 * FilterSection — collapsible section header used in the reorganized filter
 * drawer (Feb 2026 Phase 2b). Sections start expanded; users can collapse to
 * scan the drawer faster. `lockedTier` renders a small tier pill on the
 * header so users know at a glance that a section is gated.
 */
function FilterSection({ title, defaultOpen = true, lockedTier = null, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[var(--hairline)] pb-4" data-testid={testId || `section-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-2 group"
        data-testid={`section-toggle-${title.toLowerCase().replace(/\s+/g,'-')}`}
      >
        <div className="flex items-center gap-2">
          <span className="font-display text-[13px] uppercase tracking-[0.14em] font-[700] text-[#0F0F10]">{title}</span>
          {lockedTier && (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-[#B91C1C]/80 bg-[#B91C1C]/10 px-1.5 py-0.5 rounded-full">
              <Lock size={8} />
              {lockedTier === 'vip' ? 'VIP' : 'Premium'}
            </span>
          )}
        </div>
        <ChevronRight size={16} className={`text-[#8A8A93] transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="mt-3 space-y-4">{children}</div>}
    </div>
  );
}

/**
 * TierGroup — Feb 27 2026 · Phase 163. Top-level tier heading for the
 * reorganized Discovery Filters panel. Renders the group name (uppercase),
 * a subtitle, and an optional Premium/VIP lock badge. Purely visual —
 * per-filter locking is still enforced by FilterSelect + FILTER_TIER_MAP.
 * Feb 27 2026 · Phase 164 — `celebrating` prop drives a one-shot reveal
 * animation when the user just upgraded to this tier. The lock badge
 * fades out (~800ms) and a soft ring pulses around the section header.
 */
function TierGroup({ title, subtitle, lockedTier = null, testId, celebrating = false, children }) {
  return (
    <section
      className={`pt-5 ${celebrating ? 'animate-tier-unlock' : ''}`}
      data-testid={testId}
      data-celebrating={celebrating ? '1' : '0'}>
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-display text-[15px] font-[700] tracking-[0.02em] text-[#0F0F10]">{title}</h3>
        {lockedTier && (
          <span
            className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C]/85 bg-[#B91C1C]/10 px-1.5 py-0.5 rounded-full transition-opacity duration-700 ${celebrating ? 'opacity-0' : 'opacity-100'}`}
            data-testid={`tier-group-lock-${lockedTier}`}>
            <Lock size={9} />
            {lockedTier === 'vip' ? 'VIP' : 'Premium'}
          </span>
        )}
      </div>
      {subtitle && <p className="text-[11.5px] text-[#8A8A93] mb-2">{subtitle}</p>}
      {children}
    </section>
  );
}

/**
 * LockedFilterModal — brief explanation + upgrade CTA.
 * Opens when a user taps a filter their tier can't use, OR when the server
 * returns 403 with an X-Upgrade-Target header (belt + suspenders).
 */
function LockedFilterModal({ open, label, target, onClose, onUpgrade }) {
  if (!open) return null;
  const tierName = target === 'vip' ? 'VIP' : 'Premium';
  const perkLine = target === 'vip'
    ? 'VIP unlocks advanced matching — compatibility & Meet Score minimums, verified-only, active recently, and unlimited saved searches.'
    : 'Premium unlocks 13 lifestyle filters (Religion, Education, Height, Languages, and more), 5 saved searches, unlimited undo, and 5 Super Likes a day.';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="filter-lock-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#B91C1C]/10 flex items-center justify-center">
            <Star size={14} className="text-[#B91C1C]" fill="#B91C1C" />
          </div>
          <h3 className="font-display text-lg font-semibold text-[#0F0F10]">{tierName} filter</h3>
        </div>
        <p className="text-sm text-[#4B4B52] mb-2">
          <span className="font-semibold text-[#0F0F10]">{label}</span> is a {tierName} filter.
        </p>
        <p className="text-sm text-[#4B4B52] mb-5">{perkLine}</p>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold text-[#4B4B52] hover:bg-black/[0.04]">
            Not now
          </button>
          <button
            onClick={onUpgrade}
            data-testid="filter-lock-upgrade-btn"
            className="flex-1 px-4 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold btn-shine"
          >
            See {tierName}
          </button>
        </div>
      </div>
    </div>
  );
}


function FilterSelect({ label, value, onChange, options, testId, filterKey, currentTier, onLocked, searchable = false }) {  // Feb 2026 premium filter system — if this filter's required tier is
  // above the user's current tier, render a locked overlay + hijack clicks
  // to open the paywall modal instead.
  // Feb 27 2026 · Phase 159 — `searchable` variant renders a mini text
  // filter above the pill row. Used by the Interest filter since the
  // catalog grew to 123 items.
  const requiredTier = FILTER_TIER_MAP[filterKey] || 'free';
  const locked = !!filterKey && tierRank(currentTier || 'free') < tierRank(requiredTier);
  const target = requiredTier === 'vip' ? 'vip' : 'premium';
  const [q, setQ] = useState('');
  const shown = searchable && q.trim()
    ? options.filter(([v, l]) => !v || l.toLowerCase().includes(q.trim().toLowerCase()))
    : options;

  return (
    <div className="relative">
      <Label className="text-sm text-[#4B4B52] mb-2 flex items-center gap-1.5">
        {label}
        {locked && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C]/80 bg-[#B91C1C]/10 px-1.5 py-0.5 rounded-full" data-testid={`filter-lock-${filterKey}`}>
            <Lock size={9} />
            {target === 'vip' ? 'VIP' : 'Premium'}
          </span>
        )}
      </Label>
      {searchable && !locked && (
        <div className="relative mb-2">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            data-testid={`${testId}-search`}
            placeholder={`Search ${label.toLowerCase()}`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-8 text-[11px] rounded-lg bg-white border-black/[0.08] placeholder:text-zinc-400"
          />
        </div>
      )}
      <div className={`flex flex-wrap gap-1.5 ${locked ? 'opacity-50 pointer-events-none' : ''} ${searchable ? 'max-h-56 overflow-y-auto pr-1' : ''}`} data-testid={testId}>
        {shown.map(([v, l]) => (
          <button key={v} onClick={() => onChange(v)} type="button" disabled={locked}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              value === v
                ? 'brand-gradient text-white'
                : 'bg-black/[0.03] text-[#4B4B52] hover:bg-black/[0.06] border border-[var(--hairline)]'
            }`}>
            {l}
          </button>
        ))}
        {searchable && q.trim() && shown.length <= 1 && (
          <p className="text-[11px] text-zinc-500 italic w-full mt-1" data-testid={`${testId}-empty`}>No matches for "{q}"</p>
        )}
      </div>
      {/* Overlay tap-catcher for locked filters — captures every touch/click
          on the disabled row and routes it to the paywall modal. Requires the
          parent to be `position: relative` (see className above) so `inset:0`
          actually anchors over the pill row. */}
      {locked && (
        <button
          type="button"
          onClick={() => onLocked?.({ label, target, filter_key: filterKey })}
          aria-label={`${label} is a ${target} filter — tap to upgrade`}
          data-testid={`filter-lock-tap-${filterKey}`}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      )}
    </div>
  );
}


/** Feb 2026 · Phase 137 — multi-select chip filter with the same premium
 *  tier-gating pattern as `FilterSelect`. `values` is an array of the
 *  selected option values; empty array means "Any". Used by Body Type
 *  today; suitable for any future multi-preference filter. */
function FilterMulti({ label, values, onChange, options, testId, filterKey, currentTier, onLocked }) {
  const requiredTier = FILTER_TIER_MAP[filterKey] || 'free';
  const locked = !!filterKey && tierRank(currentTier || 'free') < tierRank(requiredTier);
  const target = requiredTier === 'vip' ? 'vip' : 'premium';
  const set = new Set(Array.isArray(values) ? values : []);
  const toggle = (v) => {
    if (set.has(v)) onChange(Array.from(set).filter((x) => x !== v));
    else onChange([...Array.from(set), v]);
  };
  const isActive = (v) => (v === '' ? set.size === 0 : set.has(v));
  return (
    <div className="relative">
      <Label className="text-sm text-[#4B4B52] mb-2 flex items-center gap-1.5">
        {label}
        {locked && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C]/80 bg-[#B91C1C]/10 px-1.5 py-0.5 rounded-full" data-testid={`filter-lock-${filterKey}`}>
            <Lock size={9} />
            {target === 'vip' ? 'VIP' : 'Premium'}
          </span>
        )}
      </Label>
      <div className={`flex flex-wrap gap-1.5 ${locked ? 'opacity-50 pointer-events-none' : ''}`} data-testid={testId}>
        {options.map(([v, l]) => (
          <button key={v || '__any__'} onClick={() => (v === '' ? onChange([]) : toggle(v))}
            type="button" disabled={locked}
            data-testid={`${testId}-${(v || 'any').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              isActive(v)
                ? 'brand-gradient text-white'
                : 'bg-black/[0.03] text-[#4B4B52] hover:bg-black/[0.06] border border-[var(--hairline)]'
            }`}>
            {l}
          </button>
        ))}
      </div>
      {locked && (
        <button
          type="button"
          onClick={() => onLocked?.({ label, target, filter_key: filterKey })}
          aria-label={`${label} is a ${target} filter — tap to upgrade`}
          data-testid={`filter-lock-tap-${filterKey}`}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      )}
    </div>
  );
}
